import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, Fade, Paper } from "@mui/material";
import { useSelector } from "react-redux";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import CommonLoader from "../../../common_components/CommonLoader";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import { toast } from "../../../common_components/Toast/ToastUtils";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { DEFAULT_REPORT_NAME, buildQuerySummary, createDefaultBuilderState } from "../reportBuilder.utils";
import { NETWORKS, getNetwork } from "../reportBuilder.networks";
import { selectToken } from "../../../redux/authSlice";
import reportBuilderService from "../../../services/reportBuilder.service";
import BuilderControlsPanel, { isMetricAvailable, metricId, sourceFor } from "./BuilderControlsPanel";
import ReportPreview from "./ReportPreview";
import AskAiDrawer, { toBuilderNetworkKey } from "./AskAiDrawer";
import { buildNetworkReportPayload } from "../reportPayload";
import { toGeneratedReport } from "../reportResponse";
/** The half-pill tab that hides / shows the controls panel. */
const panelTabStyle = {
    width: 32,
    height: 44,
    padding: 0,
    border: "1px solid #FFC8E9",
    borderRight: "none",
    borderRadius: "22px 0 0 22px",
    background: "#FFFFFF",
    color: "#FF008E",
    boxShadow: "-2px 2px 8px rgba(16, 24, 40, 0.12)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};
/** API network key → builder key, where the two differ. */
const API_NETWORK_KEYS = { ga: "google_analytics" };
/** API row → builder row; a network the builder has no columns for is dropped. */
const toOfferedNetwork = (network) => {
    const key = API_NETWORK_KEYS[network.network] || network.network;
    if (!NETWORKS.some((def) => def.key === key))
        return null;
    return {
        key,
        label: network.label,
        metrics: (network.metrics || []).map((metric) => ({
            id: metricId(key, metric.key),
            key: metric.key,
            label: metric.label,
            columnLabel: metric.column_label,
            kind: metric.kind,
            requiresDimensions: metric.requires_dimensions || [],
            networkKey: key,
            networkLabel: network.label,
        })),
        filterFields: network.filter_fields || [],
        enumFields: (network.enum_fields || []).map((field) => ({
            key: field.key,
            label: field.label,
            values: field.values,
            singleSelect: Boolean(field.single_select),
        })),
        sourceChoices: (network.source_choices || []).map((choice) => ({
            key: choice.key,
            label: choice.label,
            unsupportedMetrics: choice.unsupported_metrics || [],
            unsupportedFilterFields: choice.unsupported_filter_fields || [],
            unsupportedEnumFields: choice.unsupported_enum_fields || [],
        })),
        defaultSource: network.default_source_choice ?? null,
        breakdownFields: (network.breakdown_fields || []).map((field) => ({
            key: field.key,
            label: field.label,
            unsupportedMetrics: field.unsupported_metrics || [],
        })),
        operators: (network.operators || []),
    };
};
/** Breakdown checkboxes ticked when a network is switched on (Meta: Campaign Name). */
const DEFAULT_BREAKDOWNS = { meta: ["campaign_name"] };
/** Add a network with its default breakdowns. Columns are the user's to tick. */
const withNetwork = (prev, key) => {
    if (!getNetwork(key) || prev.network_keys.includes(key))
        return prev;
    const defaultBreakdowns = DEFAULT_BREAKDOWNS[key];
    return {
        ...prev,
        network_keys: [...prev.network_keys, key],
        ...(defaultBreakdowns
            ? { breakdowns_by_network: { ...prev.breakdowns_by_network, [key]: defaultBreakdowns } }
            : {}),
    };
};
/** Drop a network and everything that belonged to it — columns, filters, breakdowns. */
const withoutNetwork = (prev, key) => {
    const { [key]: _filters, ...filters } = prev.filters_by_network;
    const { [key]: _breakdowns, ...breakdowns } = prev.breakdowns_by_network || {};
    const { [key]: _enumFilters, ...enumFilters } = prev.enum_filters_by_network || {};
    const { [key]: _source, ...sources } = prev.source_by_network || {};
    return {
        ...prev,
        network_keys: prev.network_keys.filter((k) => k !== key),
        filters_by_network: filters,
        breakdowns_by_network: breakdowns,
        enum_filters_by_network: enumFilters,
        source_by_network: sources,
        column_order: prev.column_order.filter((id) => !id.startsWith(`${key}.`)),
    };
};
/**
 * Merge a picker result into an ordered list: keep the order the user arranged
 * for anything still selected, then append the new picks in catalog order.
 */
const mergeSelection = (currentOrder, picked, catalogOrder) => {
    const kept = currentOrder.filter((id) => picked.includes(id));
    const added = picked.filter((id) => !kept.includes(id));
    if (catalogOrder) {
        added.sort((a, b) => (catalogOrder.get(a) ?? 0) - (catalogOrder.get(b) ?? 0));
    }
    return [...kept, ...added];
};
/**
 * Fields that change what the data looks like — a change to any of these marks
 * the generated preview stale.
 */
const selectionKeyOf = (state) => JSON.stringify({
    networks: state.network_keys,
    columns: state.column_order,
    dimensions: state.dimension_ids,
    filters: state.filters_by_network,
    breakdowns: state.breakdowns_by_network,
    enumFilters: state.enum_filters_by_network,
    sources: state.source_by_network,
    // Custom metrics are not here: each add / edit / delete is stored and
    // applied by its own API call, so it never calls for a regenerate.
    start: state.period_start,
    end: state.period_end,
    compareStart: state.compare_start,
    compareEnd: state.compare_end,
});
const ReportBuilder = ({ mode, initialState = null, onSnapshot, showValidation = false, refreshToken = 0, generateToken = 0, isRefreshing = false, periodStart, periodEnd, compareStart, compareEnd, reportName, controlsOpen = true, onSave, saving = false, onClone, cloning = false, setControlsOpen, clientId = "", clientName = "", initialData = null, askAiOpen = false, setAskAiOpen, onSuggestName, onSuggestPeriod, aiReportActions, }) => {
    const isView = mode === "view";
    const token = useSelector(selectToken) || "";
    // ── Payload check (before POST /network-report is wired in) ─────────
    // runGenerate is a stable callback, so it reads the client through a ref.
    const clientIdRef = useRef(clientId);
    clientIdRef.current = clientId;
    const tokenRef = useRef(token);
    tokenRef.current = token;
    // …and the offered networks (Email's source choices) the same way.
    const availableNetworksRef = useRef(null);
    const [state, setState] = useState(() => initialState || createDefaultBuilderState());
    const [report, setReport] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [generatedKey, setGeneratedKey] = useState(null);
    const isAnyRefreshing = refreshing || Boolean(isRefreshing);
    // Edit / view arrive with a saved selection — hydrate and auto-generate so
    // the user lands on the report rather than an empty canvas.
    const hydratedRef = useRef(false);
    useEffect(() => {
        if (!initialState || hydratedRef.current)
            return;
        hydratedRef.current = true;
        setState(initialState);
    }, [initialState]);
    const querySummary = useMemo(() => buildQuerySummary(state), [state]);
    const selectionKey = useMemo(() => selectionKeyOf(state), [state]);
    const stale = Boolean(report && generatedKey && generatedKey !== selectionKey);
    const isValid = Boolean(state.report_name.trim() && state.network_keys.length > 0 && state.column_order.length > 0);
    // ── Report the current selection up to the page ──────────────────────
    useEffect(() => {
        onSnapshot?.({
            state,
            hasGenerated: Boolean(report) && !stale,
            isValid,
            generatedAt: report?.generated_at ?? null,
            refreshing: isAnyRefreshing,
            hasReport: Boolean(report),
            report,
        });
        // `onSnapshot` is a stable callback on the page side.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, report, stale, isValid, isAnyRefreshing]);
    // ── Generation ───────────────────────────────────────────────────────
    /** Resolves true only when a report was actually produced. */
    const runGenerate = useCallback(async (next, viaRefresh = false) => {
        const summary = buildQuerySummary(next);
        if (!summary.isValid) {
            toast.error(summary.message);
            return false;
        }
        if (viaRefresh)
            setRefreshing(true);
        else
            setGenerating(true);
        try {
            const payload = buildNetworkReportPayload(next, clientIdRef.current, availableNetworksRef.current || []);
            const res = await reportBuilderService.getNetworkReport(payload, tokenRef.current);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            setReport(toGeneratedReport(res.data.data, next, summary.text));
            setGeneratedKey(selectionKeyOf(next));
            return true;
        }
        catch (err) {
            setReport(null);
            toast.error(err?.message || "Could not generate the report. Please try again.");
            return false;
        }
        finally {
            setGenerating(false);
            setRefreshing(false);
        }
    }, []);
    // A hydrated (edit / view) report opens on the data the page already
    // loaded with it — the same network-report call — so opening a report
    // never runs it twice. Refresh / Regenerate pull fresh data from then on.
    const autoGeneratedRef = useRef(false);
    useEffect(() => {
        if (!initialState || !initialData || autoGeneratedRef.current)
            return;
        autoGeneratedRef.current = true;
        setReport(toGeneratedReport(initialData, initialState, buildQuerySummary(initialState).text));
        setGeneratedKey(selectionKeyOf(initialState));
    }, [initialState, initialData]);
    /**
     * Refresh driven by the page's LastUpdatedInfo. The first render is skipped
     * so mounting never fires a pull; every later bump re-pulls the collections.
     */
    const lastRefreshTokenRef = useRef(refreshToken);
    useEffect(() => {
        if (refreshToken === lastRefreshTokenRef.current)
            return;
        lastRefreshTokenRef.current = refreshToken;
        // Nothing to re-pull until a report exists for the current selection.
        if (!buildQuerySummary(state).isValid)
            return;
        runGenerate(state, true);
        // `state` is read at trigger time, not tracked — only the token fires this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshToken]);
    // ── Selection handlers ───────────────────────────────────────────────
    const patch = (changes) => setState((prev) => ({ ...prev, ...changes }));
    const handleApplyMetrics = (ids) => patch({ column_order: mergeSelection(state.column_order, ids) });
    const handleRemoveMetric = (id) => patch({ column_order: state.column_order.filter((x) => x !== id) });
    const handleChangeFilters = (networkKey, filters) => patch({
        filters_by_network: { ...state.filters_by_network, [networkKey]: filters },
    });
    const handleChangeBreakdowns = (networkKey, keys) => patch({
        breakdowns_by_network: { ...state.breakdowns_by_network, [networkKey]: keys },
    });
    const handleChangeEnumFilter = (networkKey, fieldKey, values) => patch({
        enum_filters_by_network: {
            ...state.enum_filters_by_network,
            [networkKey]: { ...state.enum_filters_by_network?.[networkKey], [fieldKey]: values },
        },
    });
    const handleChangeSource = (networkKey, source) => patch({ source_by_network: { ...state.source_by_network, [networkKey]: source } });
    const handleChangeCustomMetrics = (customMetrics) => patch({ custom_metrics: customMetrics });
    // ── Custom metrics on a saved report — their own API ─────────────────
    /** Null = closed, "new" = create, a key = edit — the preview's pencil opens it too. */
    const [customEditing, setCustomEditing] = useState(null);
    /**
     * The custom-metric API finds the report by its STORED name, so keep the
     * name it was last saved under (the header name may have been edited since).
     */
    const savedNameRef = useRef(initialState?.report_name || "");
    useEffect(() => {
        if (initialState?.report_name && !savedNameRef.current)
            savedNameRef.current = initialState.report_name;
    }, [initialState]);
    /** Store the API's list and rebuild the preview from the refreshed data. */
    const applyCustomMetricResponse = (response) => {
        const stored = (response?.custom_metrics || []).map((metric) => ({
            key: metric.key,
            name: metric.name,
            description: metric.description || "",
            format: metric.format,
            position: metric.position,
            formula: metric.formula,
        }));
        const next = { ...state, custom_metrics: stored };
        setState(next);
        if (response?.data) {
            setReport(toGeneratedReport(response.data, next, buildQuerySummary(next).text));
        }
    };
    const handleSaveCustomMetric = async (metric, isUpdate) => {
        try {
            const response = await reportBuilderService.saveCustomMetric({
                client_id: clientIdRef.current,
                report_name: savedNameRef.current,
                custom_metric: {
                    ...(isUpdate ? { key: metric.key } : {}),
                    name: metric.name,
                    description: metric.description,
                    format: metric.format,
                    position: metric.position,
                    formula: metric.formula,
                },
            }, tokenRef.current);
            const body = response?.data;
            if (!body?.success) {
                toast.error(body?.message || response?.response?.data?.message || "Could not save the custom metric.");
                return false;
            }
            applyCustomMetricResponse(body);
            toast.success(body.message || "Custom metric saved.");
            // Same as Generate: give the refreshed report the full width.
            setControlsOpen?.(false);
            return true;
        }
        catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Could not save the custom metric.");
            return false;
        }
    };
    const handleDeleteCustomMetric = async (key) => {
        try {
            const response = await reportBuilderService.deleteCustomMetric({ client_id: clientIdRef.current, report_name: savedNameRef.current, key }, tokenRef.current);
            const body = response?.data;
            if (!body?.success) {
                toast.error(body?.message || response?.response?.data?.message || "Could not remove the custom metric.");
                return false;
            }
            applyCustomMetricResponse(body);
            toast.success(body.message || "Custom metric removed.");
            return true;
        }
        catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Could not remove the custom metric.");
            return false;
        }
    };
    /**
     * Mirror the page-owned range into the builder selection. One-way only — the
     * builder never writes back, so there is no loop.
     */
    useEffect(() => {
        setState((prev) => prev.period_start === periodStart &&
            prev.period_end === periodEnd &&
            prev.compare_start === compareStart &&
            prev.compare_end === compareEnd
            ? prev
            : {
                ...prev,
                period_start: periodStart,
                period_end: periodEnd,
                compare_start: compareStart,
                compare_end: compareEnd,
            });
    }, [periodStart, periodEnd, compareStart, compareEnd]);
    /** Same one-way mirror for the header's inline name editor. */
    useEffect(() => {
        // On Edit the header name only changes through the rename API, so it
        // is already the stored one the custom-metric API looks up.
        if (mode === "edit" && reportName.trim())
            savedNameRef.current = reportName.trim();
        setState((prev) => prev.report_name === reportName ? prev : { ...prev, report_name: reportName });
    }, [reportName]);
    const handleChangeBreakdown = (dimensionIds) => patch({ dimension_ids: dimensionIds });
    // ── Networks for the client (GET /client-config) ─────────────────────
    // Fetched once per client. The API does not filter by dimension, so
    // changing the Breakdown Dimension only updates the selection locally —
    // no request.
    /** `null` until the first answer for the current client. */
    const [availableNetworks, setAvailableNetworks] = useState(null);
    availableNetworksRef.current = availableNetworks;
    const [networksLoading, setNetworksLoading] = useState(false);
    const dimensionsKey = state.dimension_ids.join(",");
    useEffect(() => {
        if (!clientId) {
            setAvailableNetworks([]);
            setNetworksLoading(false);
            return;
        }
        let cancelled = false;
        setNetworksLoading(true);
        (async () => {
            try {
                const res = await reportBuilderService.getClientConfig({ client_id: clientId }, token);
                if (cancelled)
                    return;
                if (!res?.data?.success) {
                    throw new Error(res?.data?.message || res?.response?.data?.message);
                }
                const networks = res.data.data?.networks || [];
                setAvailableNetworks(networks
                    .map(toOfferedNetwork)
                    .filter((network) => Boolean(network)));
            }
            catch (err) {
                if (cancelled)
                    return;
                setAvailableNetworks([]);
                toast.error(err?.message || "Failed to fetch the client's networks.");
            }
            finally {
                if (!cancelled)
                    setNetworksLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // `token` is read, not tracked — a token refresh must not re-fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);
    // Every listed network is active — there is no on/off switch. The selection
    // follows the list: a new network comes in with its predefined columns, one
    // no longer listed (client changed) leaves with everything it brought.
    // Re-runs on network_keys too, so Clear All brings the list straight back.
    const networkKeysKey = state.network_keys.join(",");
    useEffect(() => {
        if (networksLoading || availableNetworks === null)
            return;
        setState((prev) => {
            const listed = availableNetworks.map((network) => network.key);
            const dropped = prev.network_keys.filter((key) => !listed.includes(key));
            const added = listed.filter((key) => !prev.network_keys.includes(key));
            if (!dropped.length && !added.length)
                return prev;
            return added.reduce(withNetwork, dropped.reduce(withoutNetwork, prev));
        });
    }, [availableNetworks, networksLoading, networkKeysKey]);
    // A column that stops being available — Campaign taken out of the
    // breakdown (Account ID), or Ad Name ticked — leaves the report.
    const breakdownsKey = JSON.stringify([state.breakdowns_by_network || {}, state.source_by_network || {}]);
    useEffect(() => {
        if (!availableNetworks)
            return;
        setState((prev) => {
            const keep = prev.column_order.filter((id) => {
                for (const network of availableNetworks) {
                    const metric = network.metrics.find((m) => m.id === id);
                    if (metric) {
                        return isMetricAvailable(metric, network, prev.dimension_ids, prev.breakdowns_by_network?.[network.key] || [], sourceFor(network, prev.source_by_network?.[network.key]));
                    }
                }
                return true;
            });
            return keep.length === prev.column_order.length ? prev : { ...prev, column_order: keep };
        });
    }, [availableNetworks, dimensionsKey, breakdownsKey]);
    // ── Rail actions ────────────────────────────────────────────────────
    /** A preview exists AND still describes the current selection. */
    const reportGenerated = Boolean(report) && !stale;
    /**
     * A report has been produced at some point in this session — the action
     * reads "Regenerate Report" from then on. Cleared by Clear All, which drops
     * the preview.
     */
    const hasGeneratedOnce = Boolean(report);
    /**
     * The single rail action: save, and preview what came back. It is disabled
     * while `reportGenerated` holds (nothing has changed since the last run), so
     * by the time it can be clicked there is always something to generate.
     *
     * ONE request: save-report runs the report it stores and returns the same
     * data network-report would, so the preview is built from its response
     * rather than from a second call. `onSave` resolves to that data, or null
     * when the save failed (it shows its own error).
     */
    /**
     * Takes the selection explicitly so a caller that has just set it (the AI
     * agent's Apply) can run it in the same tick, before React re-renders.
     */
    const generateAndSave = async (next) => {
        const summary = buildQuerySummary(next);
        if (!summary.isValid) {
            toast.error(summary.message);
            return false;
        }
        const payload = buildNetworkReportPayload(next, clientIdRef.current, availableNetworksRef.current || []);
        setGenerating(true);
        try {
            const data = await onSave?.(next, payload);
            if (!data)
                return false;
            savedNameRef.current = next.report_name.trim();
            setReport(toGeneratedReport(data, next, summary.text));
            setGeneratedKey(selectionKeyOf(next));
            // Give the fresh report the full width; the header toggle brings
            // the panel back for the next change.
            setControlsOpen?.(false);
            return true;
        }
        finally {
            setGenerating(false);
        }
    };
    const handleGenerateAndSave = () => generateAndSave(state);
    // The date picker's "Apply and Generate": the first render is skipped, every
    // later bump runs Generate & Save. The range comes from the props, not from
    // `state` - the mirror effect has not copied it into `state` yet in this tick.
    const lastGenerateTokenRef = useRef(generateToken);
    useEffect(() => {
        if (generateToken === lastGenerateTokenRef.current)
            return;
        lastGenerateTokenRef.current = generateToken;
        generateAndSave({
            ...state,
            period_start: periodStart,
            period_end: periodEnd,
            compare_start: compareStart,
            compare_end: compareEnd,
        });
        // Only the token fires this; everything else is read at trigger time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generateToken]);
    const [confirmClear, setConfirmClear] = useState(false);
    /**
     * Clear All wipes the SELECTION only — networks, columns, breakdown and
     * filters. The report name stays (the user typed it) and the date range is
     * page-owned, so the sync effect restores it on the next render.
     */
    const handleClearAll = () => {
        setState((prev) => ({
            ...createDefaultBuilderState(),
            report_name: prev.report_name,
            period_start: prev.period_start,
            period_end: prev.period_end,
            compare_start: prev.compare_start,
            compare_end: prev.compare_end,
        }));
        setReport(null);
        setGeneratedKey(null);
        setConfirmClear(false);
    };
    const hasSelection = state.network_keys.length > 0 || state.column_order.length > 0;
    // ── Ask AI ──────────────────────────────────────────────────────────
    /**
     * Load the AI's plan into the builder. It replaces the SELECTION only —
     * the date range stays page-owned and nothing is saved: the rail's
     * Generate Report runs and stores it as always, so the user keeps the
     * last word. Columns the client does not offer are dropped on the way in.
     */
    const handleApplyAiPlan = (plan) => {
        const offered = availableNetworksRef.current || [];
        // The plan speaks the API's vocabulary ("ga", "spend"); the builder keys
        // networks its own way and columns as "<network>.<metric>".
        const planNetworks = plan.networks
            .map((item) => ({ ...item, key: toBuilderNetworkKey(item.network) }))
            .filter((item) => offered.some((network) => network.key === item.key));
        const columnOrder = [];
        const sources = {};
        const breakdowns = {};
        const enumFilters = {};
        const filters = {};
        for (const item of planNetworks) {
            const network = offered.find((n) => n.key === item.key);
            for (const metric of item.columns) {
                const id = metricId(item.key, metric);
                if (network.metrics.some((m) => m.id === id))
                    columnOrder.push(id);
            }
            if (item.source)
                sources[item.key] = item.source;
            if (item.breakdowns.length)
                breakdowns[item.key] = item.breakdowns;
            if (item.enum_filters.length) {
                enumFilters[item.key] = Object.fromEntries(item.enum_filters.map((f) => [f.field, f.values]));
            }
            if (item.filters.length) {
                filters[item.key] = item.filters.map((f, i) => ({
                    id: `ai-${item.key}-${i}-${Date.now()}`,
                    field_id: f.field,
                    operator: f.operator,
                    values: [f.value],
                }));
            }
        }
        // The agent's name is taken only while the report still has the
        // placeholder one - a name the user typed is theirs. Same rule the
        // page applies in onSuggestName, mirrored here so the save that
        // follows goes out under the new name in the same tick.
        const currentName = state.report_name.trim();
        const takeName = Boolean(plan.report_name) && (!currentName || currentName === DEFAULT_REPORT_NAME);
        // A period the user asked for ("last month") overrides the page's
        // picker; otherwise the picker's range stays. Compare only ever comes
        // with a period of its own.
        const blank = {
            ...createDefaultBuilderState(),
            report_name: takeName ? plan.report_name : state.report_name,
            period_start: plan.period?.start || state.period_start,
            period_end: plan.period?.end || state.period_end,
            compare_start: plan.period ? plan.compare?.start ?? null : state.compare_start,
            compare_end: plan.period ? plan.compare?.end ?? null : state.compare_end,
        };
        const withNetworks = planNetworks.map((item) => item.key).reduce(withNetwork, blank);
        const next = {
            ...withNetworks,
            column_order: columnOrder,
            dimension_ids: plan.dimensions.length ? plan.dimensions : ["day"],
            source_by_network: { ...withNetworks.source_by_network, ...sources },
            breakdowns_by_network: { ...withNetworks.breakdowns_by_network, ...breakdowns },
            enum_filters_by_network: enumFilters,
            filters_by_network: filters,
            // The plan carries the report's FULL formula-column list, already
            // checked server-side by the same validator save-report runs; the
            // save that follows stores it. A plan from before custom metrics
            // existed has none, which keeps the old behaviour.
            custom_metrics: (plan.custom_metrics || []).map((metric) => ({
                key: metric.key,
                name: metric.name,
                description: metric.description || "",
                format: metric.format,
                position: metric.position,
                formula: metric.formula,
            })),
        };
        setState(next);
        setReport(null);
        setGeneratedKey(null);
        if (takeName)
            onSuggestName?.(plan.report_name);
        // The page mirrors the range back into the builder; it is already the
        // same, so the sync effect is a no-op and nothing re-runs.
        if (plan.period)
            onSuggestPeriod?.(plan.period, plan.compare);
        // Apply runs the report straight away - the same generate-and-save the
        // rail button does - so the user lands on the data, not on a selection
        // still waiting to be run. The drawer closes so the result has the room.
        // No toast of its own: the save inside already shows one.
        setAskAiOpen?.(false);
        generateAndSave(next);
    };
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1 }, children: [networksLoading && (_jsx(Paper, { elevation: 0, sx: {
                    border: "1px solid #E4E7EC",
                    borderRadius: 2,
                    minHeight: "calc(100vh - 190px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                }, children: _jsx(CommonLoader, { className: "table-loader" }) })), _jsxs(Box, { sx: {
                    display: networksLoading ? "none" : "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    alignItems: "flex-start",
                    gap: 1,
                }, children: [_jsx(Box, { sx: { flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 1 }, children: _jsx(Paper, { elevation: 0, sx: {
                                border: "1px solid #E4E7EC",
                                borderRadius: 2,
                                p: 1.75,
                                minHeight: 420,
                            }, children: _jsx(ReportPreview, { state: state, report: report, loading: generating || isAnyRefreshing, stale: stale, querySummary: querySummary, onGenerate: () => runGenerate(state), disabled: isView || isAnyRefreshing, clientName: clientName, clientId: clientId, onEditCustomMetric: mode === "edit" ? setCustomEditing : undefined, onAskAi: setAskAiOpen ? () => setAskAiOpen(true) : undefined, title: "" }) }) }), setControlsOpen && !isView && (_jsx(Fade, { in: !controlsOpen, timeout: 250, unmountOnExit: true, children: _jsx(Box, { sx: { position: "fixed", right: 0, top: 280, zIndex: 999 }, children: _jsx(HoverTooltip, { title: "Show Controls", placement: "left", children: _jsx("button", { type: "button", "aria-label": "Show Controls", onClick: () => setControlsOpen(true), style: panelTabStyle, children: _jsx(ChevronsLeft, { size: 18 }) }) }) }) })), _jsx(Collapse, { in: controlsOpen && !isView, orientation: "horizontal", timeout: 300, unmountOnExit: true, sx: {
                            flex: "0 0 auto",
                            alignSelf: "flex-start",
                            // Above the preview table's sticky header / totals / pinned
                            // columns (z-index 10–11), which otherwise paint over the
                            // floating Selected Metrics pane; below MUI modals (1300).
                            zIndex: { lg: 20 },
                            position: { lg: "sticky" },
                            top: { lg: 8 },
                            width: { xs: "100%", lg: "auto" },
                        }, children: _jsx(Box, { sx: {
                                // The rail only — Selected Metrics floats over the
                                // preview beside it, so the preview keeps its width.
                                // A bordered card: the 344px rail plus 8px padding
                                // and the 1px border on each side.
                                width: { xs: "100%", lg: 362 },
                                flex: { xs: "1 1 auto", lg: "0 0 362px" },
                                boxSizing: "border-box",
                                p: 1,
                                bgcolor: "#F9FAFB",
                                border: "1px solid #E4E7EC",
                                borderRadius: 2,
                                position: "relative",
                            }, children: _jsx(BuilderControlsPanel
                            // Hides the whole panel — a tab on the controls' outer
                            // left edge (Google Ads' report editor).
                            , { 
                                // Hides the whole panel — a tab on the controls' outer
                                // left edge (Google Ads' report editor).
                                hideControlsTab: setControlsOpen ? (_jsx(HoverTooltip, { title: "Hide Controls", placement: "left", children: _jsx("button", { type: "button", "aria-label": "Hide Controls", onClick: () => setControlsOpen(false), style: panelTabStyle, children: _jsx(ChevronsRight, { size: 18 }) }) })) : undefined, state: state, disabled: isView || isAnyRefreshing, onApplyMetrics: handleApplyMetrics, onRemoveMetric: handleRemoveMetric, onReorderColumns: (ids) => patch({ column_order: ids }), breakdownDimensions: state.dimension_ids, onChangeBreakdown: handleChangeBreakdown, availableNetworks: availableNetworks || [], networksLoading: networksLoading, hasClient: Boolean(clientId), onChangeFilters: handleChangeFilters, onChangeBreakdowns: handleChangeBreakdowns, onChangeEnumFilter: handleChangeEnumFilter, onChangeSource: handleChangeSource, onChangeCustomMetrics: handleChangeCustomMetrics, onSaveCustomMetric: mode === "edit" ? handleSaveCustomMetric : undefined, onDeleteCustomMetric: mode === "edit" ? handleDeleteCustomMetric : undefined, customEditing: customEditing, onCustomEditingChange: setCustomEditing, onGenerateAndSave: handleGenerateAndSave, hasGeneratedOnce: hasGeneratedOnce, onClearAll: () => setConfirmClear(true), 
                                // Required fields first, then the "already up to
                                // date" case — the action has nothing to do until
                                // something in the selection changes again.
                                generateDisabledReason: !state.report_name.trim()
                                    ? "Name the report first"
                                    : !querySummary.isValid
                                        ? querySummary.message
                                        : reportGenerated
                                            ? "This report is generated and saved - change the selection to run it again"
                                            : undefined, generating: generating || isAnyRefreshing, saving: saving, hasSelection: hasSelection, onClone: onClone ? () => onClone(state) : undefined, cloning: cloning, allowCustomMetrics: mode !== "add", 
                                // The name is no longer a precondition — the Clone
                                // dialog asks for the copy's own name (and client).
                                cloneDisabledReason: state.column_order.length === 0
                                    ? "Add at least one column first"
                                    : undefined }) }) })] }), setAskAiOpen && !isView && (_jsx(AskAiDrawer, { open: askAiOpen, onClose: () => setAskAiOpen(false), clientId: clientId, clientName: clientName, availableNetworks: availableNetworks || [], state: state, onApply: handleApplyAiPlan, reportActions: aiReportActions })), _jsx(ConfirmActionModal, { open: confirmClear, title: "Clear all selections", description: "This removes every network, column, breakdown and filter you have set up. The report name and date range are kept.", confirmText: "Clear All", onConfirm: handleClearAll, onClose: () => setConfirmClear(false) })] }));
};
export default ReportBuilder;
